import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { MapPin, Route } from '@/ui/icons'
import { ItineraryViewSkeleton } from '@/ui/components'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/components'
import { WaypointResponse } from '@/shared/api/routes'
import { TripRouteScheduleRoute } from '@/shared/api/routes'
import { organizeWaypointsByDay } from '@/pages/itinerary-builder/utils/itineraryHelpers'
import { ItineraryWaypointCard } from '../cards/ItineraryWaypointCard'
import { ItineraryWaypointRow } from './ItineraryWaypointRow'
import { calculateWaypointDistance } from '../../utils/itineraryDistanceUtils'
import { validateWaypointArrivalTime } from '../../utils/itineraryTimeValidation'
import { formatDistance } from '@/modules/routes/utils/routeCalculations'
import { AddWaypointDialog } from '../modals/AddWaypointDialog'
import { WaypointTypeDropdown } from '../waypoints/WaypointTypeDropdown'
import { TripsApi } from '@/shared/api/trips'
import { useAuth } from '@/modules/auth'
import type { CreateWaypointDto } from '@/shared/api/routes'
import { isRouteStop, isDetour, type POIType } from '@/pages/itinerary-builder/utils/waypointTypeStyles'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface SortableWaypointGroupProps {
  waypointId: string
  isDragging?: boolean
  children: (props: { dragAttributes: any; dragListeners: any }) => React.ReactNode
}

function SortableWaypointGroup({ waypointId, isDragging, children }: SortableWaypointGroupProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: waypointId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragAttributes: attributes, dragListeners: listeners })}
    </div>
  )
}

interface TripItineraryViewProps {
  tripId: string
  waypoints: WaypointResponse[]
  tripStartDate: string | null
  tripEndDate: string | null
  isLoading?: boolean
  routeSchedule?: TripRouteScheduleRoute[]
  isOwner?: boolean
  selectedWaypointId?: string | null
  onWaypointSelect?: (waypointId: string) => void
  editInPanelMode?: boolean
  onUpdateWaypoint?: (waypointId: string, updates: { start_time?: string | null; end_time?: string | null; notes?: string | null; name?: string; poi_type?: POIType; poi_custom_label?: string | null }) => Promise<void>
  onWaypointAdded?: () => void
  onDeleteWaypoint?: (waypointId: string) => Promise<void>
  onReorderWaypoints?: (waypointIds: string[]) => Promise<void>
}

export function TripItineraryView({
  tripId,
  waypoints,
  tripStartDate,
  tripEndDate,
  isLoading = false,
  routeSchedule,
  isOwner = false,
  selectedWaypointId = null,
  onWaypointSelect,
  editInPanelMode = false,
  onUpdateWaypoint,
  onWaypointAdded,
  onDeleteWaypoint,
  onReorderWaypoints
}: TripItineraryViewProps) {
  const { getAccessToken } = useAuth()
  const [selectedDay, setSelectedDay] = useState<string>('1')
  const [isAddWaypointDialogOpen, setIsAddWaypointDialogOpen] = useState(false)
  const [pendingWaypointDay, setPendingWaypointDay] = useState<number | null>(null)
  const [pendingPoiType, setPendingPoiType] = useState<POIType | null>(null)
  const [pendingCustomLabel, setPendingCustomLabel] = useState<string | null>(null)
  
  // Use refs to store values immediately (synchronous) to avoid React state update timing issues
  const pendingWaypointDayRef = useRef<number | null>(null)
  const pendingPoiTypeRef = useRef<POIType | null>(null)
  const pendingCustomLabelRef = useRef<string | null>(null)

  const itineraryDays = useMemo(() => {
    const days = organizeWaypointsByDay(waypoints, tripStartDate, tripEndDate)
    return days
  }, [waypoints, tripStartDate, tripEndDate, tripId])

  // Show tabs if more than 3 days
  const showTabs = itineraryDays.length > 3

  // Reset selected day when itinerary days change
  useEffect(() => {
    if (itineraryDays.length > 0) {
      const firstDay = String(itineraryDays[0].day)
      if (selectedDay !== firstDay && !itineraryDays.find(d => String(d.day) === selectedDay)) {
        setSelectedDay(firstDay)
      }
    }
  }, [itineraryDays, selectedDay])

  // Calculate day summaries (stops, miles, drive time, route/detour counts)
  const daySummaries = useMemo(() => {
    return itineraryDays.map(day => {
      let totalDistanceMeters = 0
      let totalDurationMinutes = 0

      // Calculate distances and durations between waypoints
      for (let i = 1; i < day.waypoints.length; i++) {
        const prev = day.waypoints[i - 1]
        const curr = day.waypoints[i]
        
        const distance = calculateWaypointDistance(prev, curr, routeSchedule)
        totalDistanceMeters += distance.distanceMeters

        // Use actual Mapbox duration if available, otherwise estimate
        if (distance.durationSeconds && distance.durationSeconds > 0) {
          // Use actual duration from Mapbox route
          const minutes = Math.ceil(distance.durationSeconds / 60)
          totalDurationMinutes += minutes
        } else if (distance.distanceMeters > 0) {
          // Fallback to estimated duration based on distance (50 km/h average speed)
          const minutes = Math.ceil((distance.distanceMeters / 1000) / (50 / 60))
          totalDurationMinutes += minutes
        }
      }

      // Also add duration from waypoint stop times if available
      day.waypoints.forEach(wp => {
        if (wp.start_time && wp.end_time) {
          // Parse times and calculate duration
          const start = parseTimeToMinutes(wp.start_time)
          const end = parseTimeToMinutes(wp.end_time)
          if (start !== null && end !== null) {
            const duration = end >= start ? end - start : (24 * 60 - start) + end
            totalDurationMinutes += duration
          }
        }
      })

      // Calculate route stops vs detours
      // Route stops include: null poi_type, campsites, and meetup spots (main itinerary destinations)
      // Detours are side trips like photo spots, hikes, lunch stops, etc.
      const routeStops = day.waypoints.filter(wp => isRouteStop(wp.poi_type)).length
      const detours = day.waypoints.filter(wp => isDetour(wp.poi_type)).length

      return {
        day: day.day,
        stops: day.waypoints.length,
        miles: totalDistanceMeters * 0.000621371,
        driveTime: totalDurationMinutes,
        routeStops,
        detours
      }
    })
  }, [itineraryDays, routeSchedule])

  // Drag-and-drop state
  const [activeId, setActiveId] = useState<string | null>(null)
  const [draggingDay, setDraggingDay] = useState<number | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
    // Find which day the dragged waypoint belongs to
    const waypointId = event.active.id as string
    const day = itineraryDays.find(d => d.waypoints.some(wp => wp.id === waypointId))
    if (day) {
      setDraggingDay(day.day)
    }
  }, [itineraryDays])

  // Handle drag end - reorder waypoints within a day
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    setDraggingDay(null)

    if (!over || active.id === over.id || !onReorderWaypoints || !draggingDay) {
      return
    }

    const day = itineraryDays.find(d => d.day === draggingDay)
    if (!day) return

    // Get waypoint IDs for this day (sorted by order_index)
    const dayWaypointIds = day.waypoints
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
      .map(wp => wp.id)

    const oldIndex = dayWaypointIds.indexOf(active.id as string)
    const newIndex = dayWaypointIds.indexOf(over.id as string)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    // Reorder waypoints within this day
    const reorderedDayWaypointIds = arrayMove(dayWaypointIds, oldIndex, newIndex)

    // Reconstruct full trip waypoint order
    // Group all waypoints by day, then update this day's order
    const waypointsByDay = new Map<number, WaypointResponse[]>()
    waypoints.forEach(wp => {
      const wpDay = wp.trip_day ?? 1
      if (!waypointsByDay.has(wpDay)) {
        waypointsByDay.set(wpDay, [])
      }
      waypointsByDay.get(wpDay)!.push(wp)
    })

    // Build the full ordered array: all days in order, with this day's waypoints reordered
    const fullOrderedIds: string[] = []
    const allDays = Array.from(waypointsByDay.keys()).sort((a, b) => a - b)

    for (const dayNum of allDays) {
      if (dayNum === draggingDay) {
        // Use the reordered waypoint IDs for this day
        fullOrderedIds.push(...reorderedDayWaypointIds)
      } else {
        // Use original order for other days
        const otherDayWaypoints = waypointsByDay.get(dayNum)!
          .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        fullOrderedIds.push(...otherDayWaypoints.map(wp => wp.id))
      }
    }

    // Call the reorder handler with full trip order
    try {
      await onReorderWaypoints(fullOrderedIds)
    } catch (error) {
    }
  }, [itineraryDays, waypoints, onReorderWaypoints, draggingDay])

  if (isLoading) {
    return <ItineraryViewSkeleton />
  }

  if (itineraryDays.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
          <div className="h-12 w-12 rounded-full bg-earth-100 dark:bg-earth-800 flex items-center justify-center">
            <Route className="h-6 w-6 text-earth-400" aria-hidden />
          </div>
          <div>
            <p className="text-sm font-medium text-earth-700 dark:text-earth-300">No waypoints planned yet</p>
            <p className="text-xs text-earth-500 dark:text-earth-500 mt-0.5">
              Add stops, campsites, and meetup spots to build your route
            </p>
          </div>
          {isOwner && (
            <WaypointTypeDropdown
              onSelect={(poiType, customLabel) => handleAddWaypoint(1, poiType, customLabel)}
              align="center"
            />
          )}
        </div>
        <AddWaypointDialog
          isOpen={isAddWaypointDialogOpen}
          onClose={() => {
            setIsAddWaypointDialogOpen(false)
            setPendingWaypointDay(null)
            setPendingPoiType(null)
            setPendingCustomLabel(null)
            // Also clear refs
            pendingWaypointDayRef.current = null
            pendingPoiTypeRef.current = null
            pendingCustomLabelRef.current = null
          }}
          tripId={tripId}
          onWaypointAdded={onWaypointAdded}
          // Use refs to get the most up-to-date values (synchronous)
          // Fallback to state if refs are null (for backwards compatibility)
          initialPoiType={pendingPoiTypeRef.current !== null ? pendingPoiTypeRef.current : pendingPoiType}
          initialTripDay={pendingWaypointDayRef.current !== null ? pendingWaypointDayRef.current : pendingWaypointDay}
          initialCustomLabel={pendingCustomLabelRef.current !== null ? pendingCustomLabelRef.current : pendingCustomLabel}
        />
      </div>
    )
  }

  // Format date for header: "Day 2 Sunday, Feb 1, 2026"
  const formatDayHeader = (day: typeof itineraryDays[0]): string => {
    const dateStr = day.dateRange || day.date
    
    // If dateStr already contains "Day X", use it as is
    if (dateStr.startsWith('Day ')) {
      return dateStr
    }
    
    // Parse the date string (format: "Friday, Nov 29")
    const currentYear = new Date().getFullYear()
    const dateMatch = dateStr.match(/([A-Za-z]+day), ([A-Za-z]+) (\d+)/)
    
    if (dateMatch) {
      const [, dayName, month, dayNum] = dateMatch
      return `Day ${day.day} ${dayName}, ${month} ${dayNum}, ${currentYear}`
    }
    
    // Fallback
    return `Day ${day.day} ${dateStr}`
  }

  // Handle waypoint type selection and creation
  // Note: Waypoints require coordinates, so we'll open the AddWaypointDialog
  // and pre-populate the type selection
  const handleAddWaypoint = (day: number, poiType: POIType, customLabel?: string) => {
    if (!isOwner) return

    // Store in refs immediately (synchronous) to avoid React state update timing issues
    pendingWaypointDayRef.current = day
    pendingPoiTypeRef.current = poiType
    pendingCustomLabelRef.current = customLabel || null
    
    // Also update state for UI consistency
    setPendingWaypointDay(day)
    setPendingPoiType(poiType)
    setPendingCustomLabel(customLabel || null)
    
    // Open the AddWaypointDialog with pre-populated type and day
    setIsAddWaypointDialogOpen(true)
  }

  // Format duration: "1h 25m" or "45m"
  const formatDuration = (minutes: number): string => {
    if (minutes === 0) return '0m'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
    return `${mins}m`
  }

  // Format distance: "35.0 mi"
  const formatDistance = (miles: number): string => {
    if (miles === 0) return '0.0 mi'
    return `${miles.toFixed(1)} mi`
  }

  // Render a single day's content
  const renderDayContent = (day: typeof itineraryDays[0], dayIndex: number) => {
    const summary = daySummaries[dayIndex]
    
    return (
      <div key={day.day} className="space-y-5 mb-8">
        {/* Day Header with date, summary statistics, and Add Stop button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Date for day */}
            <div className="text-body-sm font-medium text-earth-900 dark:text-foreground">
              {formatDayHeader(day)}
            </div>
            
            {/* Route/Detour summary */}
            {summary && (summary.routeStops > 0 || summary.detours > 0) && (
              <div className="text-body-sm text-earth-600 dark:text-earth-400">
                {summary.routeStops} {summary.routeStops === 1 ? 'route' : 'routes'}, {summary.detours} {summary.detours === 1 ? 'detour' : 'detours'}
              </div>
            )}
          </div>
          
          {/* Add Stop button with dropdown */}
          {isOwner && (
            <WaypointTypeDropdown
              onSelect={(poiType, customLabel) => handleAddWaypoint(day.day, poiType, customLabel)}
              align="end"
            />
          )}
        </div>

        {/* Waypoints */}
        {day.waypoints.length === 0 ? (
          <div className="border-2 border-dashed border-earth-200 dark:border-earth-700 rounded-lg p-6 text-center">
            <p className="text-body-sm text-earth-600 dark:text-earth-400">
              No waypoints planned for this day
            </p>
          </div>
        ) : (
          // Only enable drag-and-drop if there are multiple waypoints and reorder is available
          (day.waypoints.length > 1 && onReorderWaypoints) ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={day.waypoints
                  .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                  .map(wp => wp.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4">
                  {(() => {
                    // Group waypoints: route stops are top-level, detours nest under the previous route stop
                    const groupedWaypoints: Array<{
                      waypoint: WaypointResponse
                      detours: WaypointResponse[]
                      isRouteStop: boolean
                      originalIndex: number
                    }> = []
                    
                    let currentRouteStop: WaypointResponse | null = null
                    let currentDetours: WaypointResponse[] = []
                    
                    day.waypoints.forEach((waypoint, index) => {
                      // Route stops include: null poi_type, campsites, and meetup spots
                      const waypointIsRouteStop = isRouteStop(waypoint.poi_type)
                      
                      if (waypointIsRouteStop) {
                        // If we have a previous route stop with detours, add it to the group
                        if (currentRouteStop) {
                          groupedWaypoints.push({
                            waypoint: currentRouteStop,
                            detours: currentDetours,
                            isRouteStop: true,
                            originalIndex: index - currentDetours.length - 1
                          })
                        }
                        // Start a new route stop group
                        currentRouteStop = waypoint
                        currentDetours = []
                      } else {
                        // This is a detour - add it to the current route stop's detours
                        if (currentRouteStop) {
                          currentDetours.push(waypoint)
                        } else {
                          // Detour before any route stop - treat as standalone (shouldn't happen, but handle gracefully)
                          groupedWaypoints.push({
                            waypoint,
                            detours: [],
                            isRouteStop: false,
                            originalIndex: index
                          })
                        }
                      }
                    })
                    
                    // Add the last route stop and its detours
                    if (currentRouteStop) {
                      groupedWaypoints.push({
                        waypoint: currentRouteStop,
                        detours: currentDetours,
                        isRouteStop: true,
                        originalIndex: day.waypoints.length - currentDetours.length - 1
                      })
                    }
                    
                    // Render grouped waypoints
                    return groupedWaypoints.map((group, groupIndex) => {
                const isLastGroup = groupIndex === groupedWaypoints.length - 1
                const previousWaypoint = groupIndex > 0 
                  ? groupedWaypoints[groupIndex - 1].waypoint 
                  : null
                
                // Calculate distance from previous route stop
                const distance = previousWaypoint
                  ? calculateWaypointDistance(previousWaypoint, group.waypoint, routeSchedule)
                  : null

                // Validate arrival time
                const timeWarning = previousWaypoint && distance
                  ? validateWaypointArrivalTime(group.waypoint, previousWaypoint, distance)
                  : null

                // Calculate canMoveUp/canMoveDown for route stop
                const routeStopIndex = day.waypoints.findIndex(wp => wp.id === group.waypoint.id)
                const canMoveUpRouteStop = routeStopIndex > 0
                const canMoveDownRouteStop = routeStopIndex < day.waypoints.length - 1
                const isDragging = activeId === group.waypoint.id
                
                return (
                  <SortableWaypointGroup
                    key={group.waypoint.id}
                    waypointId={group.waypoint.id}
                    isDragging={isDragging}
                  >
                    {({ dragAttributes, dragListeners }) => (
                      <div className="space-y-4">
                        {/* Route Stop */}
                        {editInPanelMode ? (
                          <ItineraryWaypointRow
                            waypoint={group.waypoint}
                            distanceFromPrevious={distance?.distanceFormatted ?? null}
                            dayNumber={day.day}
                            index={group.originalIndex + 1}
                            isSelected={selectedWaypointId === group.waypoint.id}
                            onSelect={onWaypointSelect}
                            isDraggable
                            dragAttributes={dragAttributes}
                            dragListeners={dragListeners}
                            isOwner={isOwner}
                            onDeleteWaypoint={onDeleteWaypoint}
                          />
                        ) : (
                          <ItineraryWaypointCard
                            waypoint={group.waypoint}
                            previousWaypoint={previousWaypoint}
                            distance={distance}
                            timeWarning={timeWarning}
                            index={group.originalIndex}
                            isLast={isLastGroup && group.detours.length === 0}
                            tripId={tripId}
                            isOwner={isOwner}
                            onUpdateWaypoint={onUpdateWaypoint || (async () => {})}
                            onDeleteWaypoint={onDeleteWaypoint}
                            hasDetours={group.detours.length > 0}
                            isDraggable={true}
                            dragAttributes={dragAttributes}
                            dragListeners={dragListeners}
                          />
                        )}
                    
                    {/* Detours nested under route stop */}
                    {group.detours.length > 0 && (
                      <div className={editInPanelMode ? 'space-y-4 mt-1' : 'ml-8 space-y-2 border-l-2 border-earth-200 pl-4'}>
                        {group.detours.map((detour, detourIndex) => {
                          const isLastDetour = detourIndex === group.detours.length - 1 && isLastGroup
                          const previousDetour = detourIndex > 0 
                            ? group.detours[detourIndex - 1] 
                            : group.waypoint
                          
                          // Calculate distance from previous waypoint (route stop or previous detour)
                          const detourDistance = previousDetour
                            ? calculateWaypointDistance(previousDetour, detour, routeSchedule)
                            : null

                          const isDraggingDetour = activeId === detour.id
                          
                          return (
                            <SortableWaypointGroup
                              key={detour.id}
                              waypointId={detour.id}
                              isDragging={isDraggingDetour}
                            >
                              {({ dragAttributes: detourDragAttrs, dragListeners: detourDragListeners }) => (
                                editInPanelMode ? (
                                  <ItineraryWaypointRow
                                    waypoint={detour}
                                    distanceFromPrevious={detourDistance?.distanceFormatted ?? null}
                                    dayNumber={day.day}
                                    index={group.originalIndex + detourIndex + 2}
                                    isDetour
                                    isSelected={selectedWaypointId === detour.id}
                                    onSelect={onWaypointSelect}
                                    isDraggable
                                    dragAttributes={detourDragAttrs}
                                    dragListeners={detourDragListeners}
                                    isOwner={isOwner}
                                    onDeleteWaypoint={onDeleteWaypoint}
                                  />
                                ) : (
                                  <ItineraryWaypointCard
                                    waypoint={detour}
                                    previousWaypoint={previousDetour}
                                    distance={detourDistance}
                                    timeWarning={previousDetour && detourDistance
                                      ? validateWaypointArrivalTime(detour, previousDetour, detourDistance)
                                      : null}
                                    index={group.originalIndex + detourIndex + 1}
                                    isLast={isLastDetour}
                                    tripId={tripId}
                                    isOwner={isOwner}
                                    onUpdateWaypoint={onUpdateWaypoint || (async () => {})}
                                    onDeleteWaypoint={onDeleteWaypoint}
                                    isDetour={true}
                                    isDraggable={true}
                                    dragAttributes={detourDragAttrs}
                                    dragListeners={detourDragListeners}
                                  />
                                )
                              )}
                            </SortableWaypointGroup>
                          )
                        })}
                      </div>
                    )}
                    </div>
                  )}
                  </SortableWaypointGroup>
                )
              })
            })()}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="space-y-4">
              {(() => {
                // Group waypoints: route stops are top-level, detours nest under the previous route stop
                const groupedWaypoints: Array<{
                  waypoint: WaypointResponse
                  detours: WaypointResponse[]
                  isRouteStop: boolean
                  originalIndex: number
                }> = []
                
                let currentRouteStop: WaypointResponse | null = null
                let currentDetours: WaypointResponse[] = []
                
                day.waypoints.forEach((waypoint, index) => {
                  // Route stops include: null poi_type, campsites, and meetup spots
                  const waypointIsRouteStop = isRouteStop(waypoint.poi_type)
                  
                  if (waypointIsRouteStop) {
                    // If we have a previous route stop with detours, add it to the group
                    if (currentRouteStop) {
                      groupedWaypoints.push({
                        waypoint: currentRouteStop,
                        detours: currentDetours,
                        isRouteStop: true,
                        originalIndex: index - currentDetours.length - 1
                      })
                    }
                    // Start a new route stop group
                    currentRouteStop = waypoint
                    currentDetours = []
                  } else {
                    // This is a detour - add it to the current route stop's detours
                    if (currentRouteStop) {
                      currentDetours.push(waypoint)
                    } else {
                      // Detour before any route stop - treat as standalone (shouldn't happen, but handle gracefully)
                      groupedWaypoints.push({
                        waypoint,
                        detours: [],
                        isRouteStop: false,
                        originalIndex: index
                      })
                    }
                  }
                })
                
                // Add the last route stop and its detours
                if (currentRouteStop) {
                  groupedWaypoints.push({
                    waypoint: currentRouteStop,
                    detours: currentDetours,
                    isRouteStop: true,
                    originalIndex: day.waypoints.length - currentDetours.length - 1
                  })
                }
                
                // Render grouped waypoints
                return groupedWaypoints.map((group, groupIndex) => {
                  const isLastGroup = groupIndex === groupedWaypoints.length - 1
                  const previousWaypoint = groupIndex > 0 
                    ? groupedWaypoints[groupIndex - 1].waypoint 
                    : null
                  
                  // Calculate distance from previous route stop
                  const distance = previousWaypoint
                    ? calculateWaypointDistance(previousWaypoint, group.waypoint, routeSchedule)
                    : null

                  // Validate arrival time
                  const timeWarning = previousWaypoint && distance
                    ? validateWaypointArrivalTime(group.waypoint, previousWaypoint, distance)
                    : null

                  return (
                    <div key={group.waypoint.id} className="space-y-4">
                      {/* Route Stop */}
                      {editInPanelMode ? (
                        <ItineraryWaypointRow
                          waypoint={group.waypoint}
                          distanceFromPrevious={distance?.distanceFormatted ?? null}
                          dayNumber={day.day}
                          index={group.originalIndex + 1}
                          isSelected={selectedWaypointId === group.waypoint.id}
                          onSelect={onWaypointSelect}
                          isOwner={isOwner}
                          onDeleteWaypoint={onDeleteWaypoint}
                        />
                      ) : (
                        <ItineraryWaypointCard
                          waypoint={group.waypoint}
                          previousWaypoint={previousWaypoint}
                          distance={distance}
                          timeWarning={timeWarning}
                          index={group.originalIndex}
                          isLast={isLastGroup && group.detours.length === 0}
                          tripId={tripId}
                          isOwner={isOwner}
                          onUpdateWaypoint={onUpdateWaypoint || (async () => {})}
                          onDeleteWaypoint={onDeleteWaypoint}
                          hasDetours={group.detours.length > 0}
                          isDraggable={false}
                        />
                      )}
                      
                      {/* Detours nested under route stop */}
                      {group.detours.length > 0 && (
                        <div className={editInPanelMode ? 'space-y-4 mt-1' : 'ml-8 space-y-2 border-l-2 border-earth-200 pl-4'}>
                          {group.detours.map((detour, detourIndex) => {
                            const isLastDetour = detourIndex === group.detours.length - 1 && isLastGroup
                            const previousDetour = detourIndex > 0 
                              ? group.detours[detourIndex - 1] 
                              : group.waypoint
                            
                            // Calculate distance from previous waypoint (route stop or previous detour)
                            const detourDistance = previousDetour
                              ? calculateWaypointDistance(previousDetour, detour, routeSchedule)
                              : null

                            const detourTimeWarning = previousDetour && detourDistance
                              ? validateWaypointArrivalTime(detour, previousDetour, detourDistance)
                              : null
                            
                            return editInPanelMode ? (
                              <ItineraryWaypointRow
                                key={detour.id}
                                waypoint={detour}
                                distanceFromPrevious={detourDistance?.distanceFormatted ?? null}
                                dayNumber={day.day}
                                index={group.originalIndex + detourIndex + 2}
                                isDetour
                                isSelected={selectedWaypointId === detour.id}
                                onSelect={onWaypointSelect}
                                isOwner={isOwner}
                                onDeleteWaypoint={onDeleteWaypoint}
                              />
                            ) : (
                              <ItineraryWaypointCard
                                key={detour.id}
                                waypoint={detour}
                                previousWaypoint={previousDetour}
                                distance={detourDistance}
                                timeWarning={detourTimeWarning}
                                index={group.originalIndex + detourIndex + 1}
                                isLast={isLastDetour}
                                tripId={tripId}
                                isOwner={isOwner}
                                onUpdateWaypoint={onUpdateWaypoint || (async () => {})}
                                onDeleteWaypoint={onDeleteWaypoint}
                                isDetour={true}
                                isDraggable={false}
                              />
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })
              })()}
            </div>
          )
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {showTabs ? (
        <div className="space-y-6">
          {/* Custom Day Tabs */}
          <div className="border-b border-earth-200 dark:border-earth-700">
            <div className="flex overflow-x-auto -mx-4 sm:mx-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="flex min-w-max sm:min-w-0 px-4 sm:px-0">
                {itineraryDays.map((day, index) => {
                  const summary = daySummaries[index]
                  const isActive = selectedDay === String(day.day)
                  
                  return (
                    <div key={day.day} className="relative">
                      {index > 0 && (
                        <div className="absolute left-0 top-0 bottom-0 w-px bg-earth-200 dark:bg-earth-700" />
                      )}
                      <button
                        type="button"
                        onClick={() => setSelectedDay(String(day.day))}
                        className={`
                          relative px-6 py-3 text-left transition-colors
                          ${isActive 
                            ? 'text-adventure-600 dark:text-adventure-400' 
                            : 'text-earth-900 dark:text-foreground hover:text-adventure-600 dark:hover:text-adventure-400'
                          }
                        `}
                      >
                        <div className="font-medium">Day {day.day}</div>
                        <div className="text-xs text-earth-500 dark:text-earth-400 mt-0.5">
                          {summary?.stops || 0} {summary?.stops === 1 ? 'stop' : 'stops'}
                        </div>
                        {isActive && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-adventure-600 dark:bg-adventure-500" />
                        )}
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Day Content */}
          {itineraryDays.map((day, dayIndex) => {
            if (selectedDay !== String(day.day)) return null
            return (
              <div key={day.day}>
                {renderDayContent(day, dayIndex)}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="space-y-8">
          {itineraryDays.map((day, dayIndex) => renderDayContent(day, dayIndex))}
        </div>
      )}
      <AddWaypointDialog
        isOpen={isAddWaypointDialogOpen}
        onClose={() => {
          setIsAddWaypointDialogOpen(false)
          setPendingWaypointDay(null)
          setPendingPoiType(null)
          setPendingCustomLabel(null)
          // Also clear refs
          pendingWaypointDayRef.current = null
          pendingPoiTypeRef.current = null
          pendingCustomLabelRef.current = null
        }}
        tripId={tripId}
        onWaypointAdded={onWaypointAdded}
        // Use refs to get the most up-to-date values (synchronous)
        // Fallback to state if refs are null (for backwards compatibility)
        initialPoiType={pendingPoiTypeRef.current !== null ? pendingPoiTypeRef.current : pendingPoiType}
        initialTripDay={pendingWaypointDayRef.current !== null ? pendingWaypointDayRef.current : pendingWaypointDay}
        initialCustomLabel={pendingCustomLabelRef.current !== null ? pendingCustomLabelRef.current : pendingCustomLabel}
      />
    </div>
  )
}

/**
 * Parse time string (HH:MM or HHMM format) to minutes since midnight
 */
function parseTimeToMinutes(timeString: string | null | undefined): number | null {
  if (!timeString) return null

  // Handle both HH:MM and HHMM formats
  const normalized = timeString.replace(':', '')
  if (normalized.length !== 4) return null

  const hours = parseInt(normalized.substring(0, 2), 10)
  const minutes = parseInt(normalized.substring(2, 4), 10)

  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}
